// json-view-utils — helpers puros (sem componente) do json-view.
// Arquivo separado por exigência do react-refresh (only-export-components).

/** Serialização bruta pro botão copiar (string como veio; objeto → JSON pretty). */
export function rawText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/** Tenta interpretar uma string como JSON (objeto/array) pro JsonTree. */
export function tryParseJson(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

// AGT-09: inputs/erros reais raramente são JSON puro — são TEXTO com JSON
// embutido ("# Args do step\n```json\n{...}\n```…", "400 {\"type\":…}").
// Extrai o maior bloco JSON parseável da string pra renderizar como árvore,
// preservando o texto ao redor. Sem match → null (cai no <pre> normal).
export function extractEmbeddedJson(
  s: string,
): { before: string; obj: unknown; after: string } | null {
  // 1º: fence markdown ```json … ```
  const fence = s.match(/```json\s*([\s\S]*?)```/i);
  if (fence && fence.index !== undefined) {
    try {
      const obj: unknown = JSON.parse(fence[1]);
      if (obj !== null && typeof obj === 'object') {
        return {
          before: s.slice(0, fence.index).trimEnd(),
          obj,
          after: s.slice(fence.index + fence[0].length).trimStart(),
        };
      }
    } catch {
      /* segue pras outras heurísticas */
    }
  }
  // 2º: primeiro '{' ou '[' — tenta do ponto até o fim e até o último
  // fechamento correspondente.
  const start = s.search(/[{[]/);
  if (start === -1) return null;
  const closer = s[start] === '{' ? '}' : ']';
  const lastClose = s.lastIndexOf(closer);
  const candidates = lastClose > start ? [s.slice(start), s.slice(start, lastClose + 1)] : [s.slice(start)];
  for (const cand of candidates) {
    if (cand.length < 20) continue; // objetinho trivial não vale a árvore
    try {
      const obj: unknown = JSON.parse(cand);
      if (obj !== null && typeof obj === 'object') {
        return {
          before: s.slice(0, start).trimEnd(),
          obj,
          after: s.slice(start + cand.length).trimStart(),
        };
      }
    } catch {
      /* tenta o próximo candidato */
    }
  }
  return null;
}

/** Heurística de markdown (headings, listas, bold/itálico, links, fences,
 *  tabelas) — decide renderizar prose em vez de texto cru (SKL-D r3). */
export function looksLikeMd(s: string): boolean {
  if (/^#{1,6}\s+\S/m.test(s)) return true;
  if (/```/.test(s)) return true;
  if (/^\s*[-*]\s+\S/m.test(s) && s.includes('\n')) return true;
  if (/\*\*[^*\n]+\*\*/.test(s)) return true;
  if (/(^|\W)_[^_\n]+_(\W|$)/.test(s)) return true;
  if (/\*[^*\n]+\*/.test(s) && s.includes('\n')) return true;
  if (/`[^`\n]+`/.test(s)) return true;
  if (/\[[^\]\n]+\]\([^)\n]+\)/.test(s)) return true;
  if (/^\|.+\|\s*$/m.test(s)) return true;
  return false;
}
